/**
 * Gestão de Caixas Picking — consulta, operação e auditoria.
 *
 * Permite abrir caixa, adicionar itens, lacrar, cancelar e detalhar.
 * Não movimenta estoque. Não altera PUT/PATCH legado nem ColetorPWA.
 */

import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import record from '../../versa/record.js';
import { notify } from '../../versa/msgbox.js';
import pdf from '../../versa/pdf.js';
import view from '../views/output-picking-boxes.js';
import openPickingBoxDetailModal from '../forms/output-picking-box-detail.js';
import reportForm from '../forms/output-picking-boxes-report.js';
import {
	openCancelBoxModal,
	openCreateBoxModal,
	openAddItemsModal,
	openCloseBoxModal,
} from '../utils/output-picking-box-ops.js';
import * as types from '../types.js';

function downloadFile(base64Content, filename, mimeType) {
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mimeType });
	const url  = URL.createObjectURL(blob);
	const a    = document.createElement('a');
	a.href     = url;
	a.download = filename || 'download';
	a.click();
	URL.revokeObjectURL(url);
}

function isCountQuery(query) {
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	return qs.get('group') === 'count';
}

function pageBounds(query) {
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	const limit = Number(qs.get('limit') || 30);
	const offset = Number(qs.get('offset') || 0);
	return {
		limit: Number.isFinite(limit) ? limit : 30,
		offset: Number.isFinite(offset) ? offset : 0,
	};
}

function filtersToParams(filterData) {
	const params = {};
	const top = 10;
	for (let i = 0; i < top; i++) {
		const field = filterData?.[`field-${i}`];
		const value = filterData?.[`value-${i}`];
		if (!field || value == null || value === '') continue;
		if ([
			'company_id', 'code', 'seal_code', 'output_id',
			'status', 'user_id', 'date_from', 'date_to',
		].includes(field))
			params[field] = value;
	}
	return params;
}

const outputPickingBoxes = function() {

	const boxes_crud = crud.create({

		_lastSearchKey: null,
		_lastSearchRows: null,

		async loadBaseData() {
			const [companies, users] = await Promise.all([
				api.call(api.list_companies, this.queryAll()),
				api.call(api.list_users, this.queryAll()),
			]);
			await Promise.all([
				db.set('companies', companies || []).catch(() => {}),
				db.set('users', users || []).catch(() => {}),
			]);
		},

		async queryData(query) {
			const filterParams = filtersToParams(this.filter || {});
			const companies = filterParams.company_id
				? [{ id: filterParams.company_id }]
				: await db.list('companies').catch(() => []);
			if (!companies.length)
				return isCountQuery(query) ? 0 : [];

			const params = {
				code:      filterParams.code      || '',
				seal_code: filterParams.seal_code || '',
				output_id: filterParams.output_id || '',
				status:    filterParams.status    || '',
				user_id:   filterParams.user_id   || '',
				date_from: filterParams.date_from || '',
				date_to:   filterParams.date_to   || '',
			};

			const searchKey = JSON.stringify({
				companies: companies.map(c => c.id).sort(),
				params,
			});

			let rows = this._lastSearchKey === searchKey && Array.isArray(this._lastSearchRows)
				? this._lastSearchRows
				: null;
			if (!rows) {
				const lists = await Promise.all(companies.map(async (company) => {
					const data = await api.call(api.search_output_picking_boxes, company.id, params);
					return data?.boxes || [];
				}));
				rows = lists.flat();
				this._lastSearchKey = searchKey;
				this._lastSearchRows = rows;
			}

			if (isCountQuery(query))
				return rows.length;
			const { limit, offset } = pageBounds(query);
			return rows.slice(offset, offset + limit);
		},

		async loadRecord() { return {}; },
		async saveRecord() { return {}; },

		async reloadPage() {
			this._lastSearchKey  = null;
			this._lastSearchRows = null;
			return Object.getPrototypeOf(this).reloadPage.call(this);
		},

		findBoxInCache(company_id, output_id, box_id) {
			return (this._lastSearchRows || []).find(r =>
				String(r.company_id) === String(company_id)
				&& Number(r.output_id) === Number(output_id)
				&& String(r.id) === String(box_id)
			) || null;
		},

		async getSingleSelectedBox() {
			const ids = await this.getSelectedIds(['company_id', 'output_id', 'id']);
			if (ids.length !== 1) {
				notify('Selecione exatamente uma caixa.', 'Caixas de Picking');
				return null;
			}
			const [company_id, output_id, id] = ids[0];
			const row = this.findBoxInCache(company_id, output_id, id);
			return {
				company_id,
				output_id,
				box_id: id,
				code: row?.code || String(id),
				status: row?.status != null ? Number(row.status) : null,
				total_items: row?.total_items ?? 0,
			};
		},

		async pkboxDetail() {
			const sel = await this.getSingleSelectedBox();
			if (!sel) return;
			await openPickingBoxDetailModal(sel.company_id, sel.output_id, sel.box_id, () => this.reloadPage());
		},

		async pkboxAddItems() {
			const sel = await this.getSingleSelectedBox();
			if (!sel) return;
			if (sel.status !== 10) {
				notify('Somente caixas abertas permitem adicionar itens.', 'Adicionar Itens');
				return;
			}
			await openAddItemsModal(sel.company_id, sel.output_id, sel.box_id, sel.code, () => this.reloadPage());
		},

		async pkboxClose() {
			const sel = await this.getSingleSelectedBox();
			if (!sel) return;
			if (sel.status !== 10) {
				notify('Somente caixas abertas podem ser lacradas.', 'Lacrar Caixa');
				return;
			}
			openCloseBoxModal(
				sel.company_id, sel.output_id, sel.box_id, sel.code,
				Number(sel.total_items) || 0,
				() => this.reloadPage(),
			);
		},

		async pkboxCancel() {
			const sel = await this.getSingleSelectedBox();
			if (!sel) return;
			if (sel.status !== 10 && sel.status !== 20) {
				notify('Somente caixas abertas ou lacradas podem ser canceladas.', 'Cancelar Caixa');
				return;
			}
			openCancelBoxModal(sel.company_id, sel.output_id, sel.box_id, sel.code, () => this.reloadPage());
		},

		async handleEvents(ev) {
			const detail = ev.target.closest?.('.link-detail-box, .btn-pkbox-detail');
			if (detail) {
				ev.preventDefault();
				try {
					const [company_id, output_id, box_id] = JSON.parse(detail.getAttribute('data-id'));
					await openPickingBoxDetailModal(company_id, output_id, box_id, () => boxes_crud.reloadPage());
				} catch (err) {
					notify(err?.message || String(err), 'Erro');
				}
				return;
			}

			const addItems = ev.target.closest?.('.btn-pkbox-add-items');
			if (addItems) {
				ev.preventDefault();
				ev.stopPropagation();
				try {
					const [company_id, output_id, box_id] = JSON.parse(addItems.getAttribute('data-id'));
					const boxCode = addItems.getAttribute('data-code') || box_id;
					await openAddItemsModal(company_id, output_id, box_id, boxCode, () => boxes_crud.reloadPage());
				} catch (err) {
					notify(err?.message || String(err), 'Erro');
				}
				return;
			}

			const closeBox = ev.target.closest?.('.btn-pkbox-close');
			if (closeBox) {
				ev.preventDefault();
				ev.stopPropagation();
				try {
					const [company_id, output_id, box_id] = JSON.parse(closeBox.getAttribute('data-id'));
					const boxCode = closeBox.getAttribute('data-code') || box_id;
					const itemCount = parseInt(closeBox.getAttribute('data-items') || '0', 10);
					openCloseBoxModal(company_id, output_id, box_id, boxCode, itemCount, () => boxes_crud.reloadPage());
				} catch (err) {
					notify(err?.message || String(err), 'Erro');
				}
				return;
			}

			const cancel = ev.target.closest?.('.btn-cancel-box, .btn-pkbox-cancel');
			if (cancel) {
				ev.preventDefault();
				ev.stopPropagation();
				try {
					const [company_id, output_id, box_id] = JSON.parse(cancel.getAttribute('data-id'));
					const row = cancel.closest('tr');
					const boxCode = cancel.getAttribute('data-code')
						|| row?.querySelector('.link-detail-box')?.textContent?.trim()
						|| box_id;
					openCancelBoxModal(company_id, output_id, box_id, boxCode, () => boxes_crud.reloadPage());
				} catch (err) {
					notify(err?.message || String(err), 'Erro');
				}
				return;
			}

			if (ev.target.closest?.('tr[data-id]') && !ev.target.closest?.('input[type="checkbox"]'))
				return;

			const hashEl = ev.target.closest?.('[hash]');
			if (hashEl && hashEl.getAttribute('hash') === '#insert') return;

			Object.getPrototypeOf(this).handleEvents.call(this, ev);
		},
	});

	const fieldspecs = [
		{ index: 0, field: 'company_id', label: 'Empresa',         types: [types.datalist()],  placeholder: '', title: 'Empresa',          store: 'companies', labels: ['nick'] },
		{ index: 1, field: 'code',        label: 'Nº Caixa',        types: [types.name()],      placeholder: '', title: 'Código da caixa'   },
		{ index: 2, field: 'seal_code',   label: 'Nº Lacre',        types: [types.name()],      placeholder: '', title: 'Código do lacre'   },
		{ index: 3, field: 'output_id',   label: 'Nº Saída',        types: [types.number(0,2)], placeholder: '0', title: 'ID da saída'     },
		{ index: 4, field: 'status',      label: 'Status',          types: [types.opt_picking_box_status(), types.datalist()], placeholder: '', title: 'Status da caixa' },
		{ index: 5, field: 'user_id',     label: 'Usuário Picking', types: [types.datalist()],  placeholder: '', title: 'Usuário que abriu a caixa', store: 'users', labels: ['username'] },
		{ index: 6, field: 'date_from',   label: 'Data inicial',    types: [types.date()],      placeholder: 'dd/mm/aaaa', title: 'Abertura a partir de' },
		{ index: 7, field: 'date_to',     label: 'Data final',      types: [types.date()],      placeholder: 'dd/mm/aaaa', title: 'Abertura até'         },
		{ index: 8, field: 'opened_at',   label: 'Aberta em',       types: [types.datetime()],  placeholder: '', title: 'Abertura da caixa' },
		{ index: 9, field: 'closed_at',   label: 'Lacrada em',      types: [types.datetime()],  placeholder: '', title: 'Lacre da caixa' },
		{ index: 10, field: 'canceled_at', label: 'Cancelada em',   types: [types.datetime()],  placeholder: '', title: 'Cancelamento da caixa' },
		{ index: 11, field: 'total_items', label: 'Qtd. Itens',      types: [types.number(0)],   placeholder: '0', title: 'Linhas na caixa' },
		{ index: 12, field: 'total_quantity', label: 'Qtd. Total',   types: [types.number(0)],   placeholder: '0', title: 'Quantidade total na caixa' },
	];

	const operations = [
		{ hash: '#pkbox-detail',     label: 'Detalhar&nbsp;Caixa' },
		{ hash: '#pkbox-add-items',  label: 'Adicionar&nbsp;Itens' },
		{ hash: '#pkbox-close',      label: 'Lacrar&nbsp;Caixa' },
		{ hash: '#pkbox-cancel',     label: 'Cancelar&nbsp;Caixa' },
		{ hash: '#open-picking-box', label: 'Abrir&nbsp;Caixa' },
	];
	const reports = [
		{ hash: '#print-picking-boxes', label: 'Controle&nbsp;Caixas&nbsp;Picking' },
	];

	function openPickingBoxFromGestao() {
		const parsedFilter = filtersToParams(boxes_crud.filter || {});
		const companyId = parsedFilter.company_id;
		if (!companyId) {
			notify('Selecione a empresa no filtro da tela antes de abrir uma caixa.', 'Abrir Caixa');
			return;
		}
		openCreateBoxModal(
			companyId,
			parsedFilter.output_id || null,
			() => boxes_crud.reloadPage(),
			{ initialOutputId: parsedFilter.output_id || null },
		);
	}

	function printPickingBoxes() {
		const parsedFilter = filtersToParams(boxes_crud.filter || {});
		const companyId    = parsedFilter.company_id;
		if (!companyId) {
			notify('Selecione a empresa no filtro da tela antes de gerar o relatório.', 'Relatório');
			return;
		}

		const f = parsedFilter;
		const initial = {
			'pkbox-rpt-format': 'pdf',
			'pkbox-rpt-code':   f.code       || '',
			'pkbox-rpt-seal':   f.seal_code  || '',
			'pkbox-rpt-output': f.output_id  || '',
			'pkbox-rpt-status': f.status     || '',
			'pkbox-rpt-from':   f.date_from  || '',
			'pkbox-rpt-to':     f.date_to    || '',
		};

		record.edit(reportForm, async (data) => {
			const fmt = data['pkbox-rpt-format'] || 'pdf';
			const payload = {
				format:     fmt,
				code:       data['pkbox-rpt-code']   || null,
				seal_code:  data['pkbox-rpt-seal']   || null,
				output_id:  data['pkbox-rpt-output'] || null,
				status:     data['pkbox-rpt-status'] || null,
				date_from:  data['pkbox-rpt-from']   || null,
				date_to:    data['pkbox-rpt-to']     || null,
			};
			try {
				const resp = await api.report_output_picking_boxes(companyId, payload);
				const result = await resp.json();
				if (!resp.ok) {
					notify(result?.error || 'Erro ao gerar relatório.', 'Erro');
					return;
				}
				if (fmt === 'pdf' && result.pdf) {
					pdf(result.pdf);
				} else if (fmt === 'csv' && result.csv) {
					downloadFile(result.csv, result.filename || 'caixas-picking.csv', 'text/csv;charset=utf-8');
					notify('Download iniciado.', 'Sucesso');
				} else {
					notify('Nenhum dado para o relatório.', 'Aviso');
				}
			} catch (err) {
				notify(err?.message || String(err), 'Erro');
			}
		}, initial, false);
	}

	router.set('#pkbox-detail', () => boxes_crud.pkboxDetail());
	router.set('#pkbox-add-items', () => boxes_crud.pkboxAddItems());
	router.set('#pkbox-close', () => boxes_crud.pkboxClose());
	router.set('#pkbox-cancel', () => boxes_crud.pkboxCancel());
	router.set('#open-picking-box', openPickingBoxFromGestao);
	router.set('#print-picking-boxes', printPickingBoxes);

	boxes_crud.run(
		'output-picking-boxes',
		'Gestão de Caixas Picking',
		document.getElementById('workarea'),
		view,
		null,
		fieldspecs,
		operations,
		reports,
		{
			default_columns: '0,1,2,3,4,5,8,9,10,11,12',
		},
	);
};

export default outputPickingBoxes;
